// reCAPTCHA Enterprise (v3, score-based) helper.
//
// Self-loads the Enterprise script once, then runs grecaptcha.enterprise.execute
// for the given action. Returns the token to send to the backend as `g-token`,
// which the Recaptcha middleware verifies via projects.assessments.create.
//
// Shared by every reCAPTCHA entry point (signin, landing/marketing/cooperation
// hero + verify, change-phone, verify flows) after replacing the legacy v2
// invisible widget.

const SITE_KEY = process.env.NEXT_PUBLIC_CAPTCHA_PUBLIC_KEY;

let scriptPromise;

function loadEnterpriseScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA Enterprise unavailable on the server'));
  }

  if (window.grecaptcha && window.grecaptcha.enterprise) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-recaptcha-enterprise]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load reCAPTCHA Enterprise')),
      );
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-recaptcha-enterprise', 'true');
    script.onload = () => resolve();
    script.onerror = () => {
      // Allow a later attempt to retry loading.
      scriptPromise = undefined;
      reject(new Error('Failed to load reCAPTCHA Enterprise'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Run grecaptcha.enterprise.execute, retrying on transient failures.
 *
 * Google's guidance for BROWSER_ERROR (the script couldn't complete an execute due to a
 * client-side network failure/timeout) is to retry execute(). Common in restricted
 * environments (in-app browsers, ad/content blockers) where the call to
 * www.google.com/recaptcha intermittently fails. Bounded retries with backoff.
 *
 * @param {string} action
 * @param {number} attempts - total tries including the first (default 3)
 * @param {number} backoffMs - initial backoff, doubled each retry (default 300)
 * @returns {Promise<string>}
 */
function executeWithRetry(action, attempts = 3, backoffMs = 300) {
  const run = (remaining, delay) =>
    window.grecaptcha.enterprise.execute(SITE_KEY, { action }).catch((error) => {
      if (remaining <= 1) {
        throw error;
      }
      return new Promise((r) => setTimeout(r, delay)).then(() =>
        run(remaining - 1, delay * 2),
      );
    });

  return run(attempts, backoffMs);
}

/**
 * Execute reCAPTCHA Enterprise for the given action and resolve with the token.
 *
 * @param {string} action - e.g. 'sms_verification' (must match backend expectedAction)
 * @returns {Promise<string>} the reCAPTCHA token to send as `g-token`
 */
export async function executeRecaptchaEnterprise(action) {
  await loadEnterpriseScript();

  // grecaptcha.enterprise.ready is callback-based, so wrap it in a Promise.
  return new Promise((resolve, reject) => {
    window.grecaptcha.enterprise.ready(() => {
      executeWithRetry(action).then(resolve, reject);
    });
  });
}
